import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { IoMdCloseCircleOutline } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import "./style.css"
import ApiRequestService from '../../component/ApiRequestService';
import AssignmentDetailModal from '../AssignmentMatrix/Modal/AssignmentDetailModal';
import DeleteDialog from '../DeleteDialog';
import { NotificationManager } from 'react-notifications';
import { MdEdit } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import SFCustomTable from '../../component/custom/SFCustomTable';
import { browserHistory } from 'react-router';
import { AppProperties } from '../../common/function';
import CustomToggle from '../../component/CustomToggle';
import { useLibrarySync, UnsyncedCategoriesDialog, ReadOnlyActionsCell, SyncedAtSourceCell, SyncLoadingCell, refuseEmptyRowSync, hasValidLibraryIds } from './LibrarySync';



const AssignmentMatrixLibrary = () => {

    const appProperties = AppProperties();

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [librarySubjects, setLibrarySubjects] = useState([]);
    const [editSubject, setEditSubject] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);

    const [showSubjectDeleteDialog, setShowSubjectDeleteDialog] = useState(false);

    const {
        syncAccess,
        syncLoading,
        syncStatusLoading,
        loadSyncStatus,
        reloadSyncStatus,
        getSyncInfo,
        toggleSync,
        unsyncedCategories,
        clearUnsyncedCategories,
    } = useLibrarySync({
        statusUrl: '/librarySyncSubjectsStatus',
        statusMethod: 'get',
        toggleUrl: '/librarySyncEnableSubject',
    });

    // The sync switch is a superadmin control; /amlSyncSwitch answers who that
    // is, and the column is dropped rather than disabled so the table does not
    // carry a dead 180px for everyone else.
    const subjectColumns = ([
        {
            name: <div className='sf-data-table-header'>ID</div>,
            selector: row => row.id,
            sortable: true,
        },
        {
            name: <div className='sf-data-table-header'>Subject Name</div>,
            selector: row => row.name,
            sortable: true,
            grow: 2
        },
        {
            name: <div className='sf-data-table-header'># of Topics</div>,
            selector: row => row.total_topics_count,
            sortable: true,
            grow: 2
        },
        {
            name: <div className='sf-data-table-header'># of Matrix</div>,
            selector: row => row.total_matrix_count,
            sortable: true,
            grow: 2
        },
        {
            name: <div className='sf-data-table-header'># of Assignments</div>,
            selector: row => row.total_quiz_count,
            sortable: true,
            grow: 2
        },
        {
            name: <div className='sf-data-table-header'>Created By</div>,
            selector: row => row.created_by,
            sortable: true,
            grow: 2
        },
        {
            id: 'sync',
            name: <div className='sf-data-table-header'>Sync</div>,
            sortable: false,
            width: '180px',
            cell: (row) => {
                let syncInfo = getSyncInfo(row.id);
                // Rows that arrived from another university are read-only here.
                // The row is the reliable half of this: the status call beside
                // it can fail, and a toggle must not appear when it does. Asked
                // before the loading check, since the row answers it already.
                if (row.is_synced || syncInfo.is_synced) {
                    return <SyncedAtSourceCell
                        amlSubjectID={row.id}
                        sourcePath={`AssignmentMatrixLibrarySubject?id=${row.id}&name=${encodeURIComponent(row.name)}`}
                    />;
                }
                if (syncStatusLoading) {
                    return <SyncLoadingCell />;
                }
                return (
                    <div style={{ marginTop: "8px" }}>
                        <CustomToggle
                            Enabled={syncInfo.enable_sync || false}
                            tristate={syncInfo.some_sync ? 1 : false}
                            onEnableChecked={() => onEnableSubjectSync(row)}
                            onDisableChecked={() => onDisableSubjectSync(row)}
                            onHandleMiddleredirect={() => { }}
                            syncStatus={syncInfo.sync_status_code || 0} // 0-off, 1-inprogress, 2-success, 3-failure
                            sourceRedirect={false}
                            type="switch"
                            id={"aml-subject-sync-" + row.id}
                        />
                    </div>
                );
            },
        },
        {
            name: <div className='sf-data-table-header'>Actions</div>,
            sortable: false,
            cell: (row, rowIndex) => {
                // A subject this university received is the source's to change.
                // Read off the row, not the sync status beside it: a failed
                // status call must not leave the write controls live.
                if (row.is_synced) {
                    return <ReadOnlyActionsCell />;
                }
                return (
                    <div>
                        <IconButton onClick={() => handleEditSubjectClick(row)}>
                            <MdEdit color='#3d5e9c' />
                        </IconButton>
                        <IconButton onClick={() => {
                            setShowSubjectDeleteDialog(true);
                            setSelectedSubject(row);
                        }}>
                            <FiTrash2 color='#bb0000' />
                        </IconButton>
                    </div>
                )
            },
            sortable: false,
        },
    ].filter(column => syncAccess || column.id !== 'sync'));

    useEffect(() => {
        getLibrarySubjects();
        loadSyncStatus();
    }, [])

    const onEnableSubjectSync = (row) => {
        if (refuseEmptyRowSync('subject', row)) return;
        toggleSync(row.id, true, { aml_subject_id: row.id });
    }

    const onDisableSubjectSync = (row) => {
        toggleSync(row.id, false, { aml_subject_id: row.id });
    }

    const getLibrarySubjects = () => {
        ApiRequestService({
            method: 'get',
            url: '/getAMLSubjects',
        }).then(res => {
            if (res && res.data && res.data.data) {
                setLibrarySubjects(res.data.data);
            } else {
                setLibrarySubjects([]);
            }
        })
    }


    const onAddLibrarySubject = (newData) => {
        setLibrarySubjects([...librarySubjects, newData]);
        setShowAddDialog(false);
        reloadSyncStatus();
    }

    const onEditLibrarySubject = (newData) => {
        let index = librarySubjects.findIndex(subj => subj.id === newData.id);
        if (index > -1) {
            let updatedLibrarySubjects = [...librarySubjects];
            updatedLibrarySubjects[index] = newData;
            setLibrarySubjects(updatedLibrarySubjects);
        }
        setEditSubject(null);
        setShowAddDialog(false);
        reloadSyncStatus();
    }


    const handleAddSubjectClick = () => {
        setEditSubject(null);
        setShowAddDialog(true);
    }

    const handleEditSubjectClick = (subject) => {
        setEditSubject(subject);
        setShowAddDialog(true);
    }


    const handleDeleteSubject = () => {
        if (!hasValidLibraryIds(selectedSubject?.id)) {
            NotificationManager.error("Could not delete the subject: missing subject id", "ERROR");
            return;
        }
        ApiRequestService({
            method: 'post',
            url: '/deleteAMLSubject',
            data: {
                id: selectedSubject.id,
            }
        }).then(res => {
            // A refused delete is a 200 carrying a non-zero status, so the
            // status is what decides. Nothing said so before: a refusal left
            // the dialog open with no message, which reads as a dead button.
            if (!res || !res.data || res.data.status !== 0) {
                NotificationManager.error(
                    (res && res.data && res.data.message) || "Could not delete the subject", "ERROR");
                return;
            }

            // Closing does not hang off finding the row any more - a lookup
            // miss used to leave the dialog up after a delete that worked.
            setLibrarySubjects(current => current.filter(ls => ls.id !== selectedSubject.id));
            setShowSubjectDeleteDialog(false);
            setSelectedSubject(null);
            reloadSyncStatus();
            NotificationManager.success("Successfully deleted the subject");
        }).catch(err => {
            console.error("Error deleting library subject:", err);
            NotificationManager.error("Could not delete the subject", "ERROR");
        })
    }



    return (
        <div className='assign-lib-cont'>
            <Typography fontWeight={500} fontSize={24}>Assignment Matrix Library</Typography>

            <div style={{ marginTop: '1rem' }}>
                <SFCustomTable
                    loading={syncLoading}
                    selectableRows={false}
                    columns={subjectColumns}
                    data={librarySubjects}
                    rightContent={
                        <div>
                            <Button variant='contained' onClick={handleAddSubjectClick}>Add Subject</Button>
                        </div>
                    }
                    topPagination={false}
                    bottomPagination={true}
                    handleRowClick={(subject) => {
                        let baseFolder = appProperties.baseFolder;
                        let url = `${baseFolder}AssignmentMatrixLibrarySubject?id=${subject.id}&name=${encodeURIComponent(subject.name)}`
                        browserHistory.push(url);
                    }}
                    defaultRowsPerPage={20}
                    showSearch={true}
                    searchPlaceholder={"Search by subject name"}
                    searchByFields={["name"]}
                />
            </div>

            <AddSubjectDialog
                open={showAddDialog}
                editSubject={editSubject}
                handleClose={() => {
                    setShowAddDialog(false);
                    setEditSubject(null);
                }}
                onAddLibrarySubject={onAddLibrarySubject}
                onEditLibrarySubject={onEditLibrarySubject}
            />


            <UnsyncedCategoriesDialog
                unsyncedCategories={unsyncedCategories}
                onClose={clearUnsyncedCategories}
            />

            <DeleteDialog
                showDialog={showSubjectDeleteDialog}
                title={`Delete Subject`}
                description={
                    <div>
                        <div>Are you sure you want to delete "{selectedSubject?.name}" subject?</div>
                        {selectedSubject?.total_quiz_count > 0 ?
                            <div>
                                <br />
                                <strong>WARNING!</strong>: Deleting this subject will also delete all topics and assignments linked to it.
                            </div>
                            : null}
                    </div>
                }
                onCancel={() => {
                    setShowSubjectDeleteDialog(false);
                }}
                onDelete={() => {
                    handleDeleteSubject();
                }}
            />

        </div>
    );
}

const AddSubjectDialog = ({ open, handleClose, editSubject, onAddLibrarySubject, onEditLibrarySubject }) => {
    const [name, setName] = useState("");

    useEffect(() => {
        if (editSubject) {
            setName(editSubject.name);
        } else {
            setName("");
        }
    }, [editSubject])

    const addLibrarySubject = () => {
        ApiRequestService({
            method: 'post',
            url: '/addAMLSubject',
            data: {
                name: name,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                onAddLibrarySubject(res.data.data);
            }
        })
    }

    const editLibrarySubject = () => {
        let payload = {
            ...editSubject,
            name: name,
        }
        ApiRequestService({
            method: 'post',
            url: '/editAMLSubject',
            data: payload
        }).then(res => {
            if (res && res.data && res.data.data) {
                onEditLibrarySubject(payload);
            }
        })
    }

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Typography fontSize={18} fontWeight={500}>
                    New Subject
                </Typography>
            </DialogTitle>
            <IconButton
                aria-label="close"
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                })}
                onClick={handleClose}
            >
                <IoMdCloseCircleOutline fontSize={24} />
            </IconButton>
            <DialogContent>
                <TextField
                    placeholder='Enter subject name'
                    sx={{
                        width: "100%",
                    }}
                    value={name}
                    onChange={event => {
                        setName(event.target.value);
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={handleClose}>
                    Close
                </Button>
                <Button variant='contained' onClick={() => {
                    if (editSubject) {
                        editLibrarySubject();
                    } else {
                        addLibrarySubject();
                    }
                }}
                    disabled={name === ""}>
                    {editSubject ? "Update" : "Add"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


export default AssignmentMatrixLibrary